import classNames from 'classnames'
import { useCallback, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { InView } from 'react-intersection-observer'
import { DropdownCard, DropdownContent, Scrollbar, SpinnerLoader } from 'shared/ui'
import { TextField, makeTextFieldAutoFocus } from 'shared/ui/TextField'
import { CreateTagButton } from 'widgets/TagsDropdown/ui/CreateTagButton/CreateTagButton'
import { TagElement } from 'widgets/TagsDropdown/ui/TagElement/TagElement'
import { useTagsDropdownContext } from 'widgets/TagsDropdown/context/TagsDropdownContext'
import { TagDropdownCard } from 'widgets/TagsDropdown/ui/TagDropdownCard/TagDropdownCard'

import styles from './styles.module.scss'

export const TagsDropdownContent = observer(() => {
  const {
    controlStore,
    searchStore,
    searchPlaceholder,
    cantCreate,
    searchTagsList,
    isFormFieldVariant,
    showContactsCount,
    disabledSearch,
    loading,
    createTag,
    addTag,
    removeTag,
  } = useTagsDropdownContext()

  const loadMore = useCallback(() => {
    const loadMore = !searchStore.loading && searchStore.hasMore

    if (loadMore) searchStore.loadMore()
  }, [searchStore.loading && searchStore.hasMore])

  useEffect(() => {
    controlStore.syncIds()

    return () => {
      searchStore.reset()
    }
  }, [])

  useEffect(() => {
    if (!controlStore.synced) return

    searchStore.searchTags(searchStore.term, controlStore.ids)
  }, [controlStore.synced, searchStore.term])

  return (
    <DropdownContent className={styles.dropdownContentWrap}>
      <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={150}>
        <div
          className={classNames(styles.selectedTagsWrap, {
            [styles.formField]: isFormFieldVariant,
          })}
          aria-label='AddedTagsListItems'
        >
          {controlStore.synced &&
            controlStore.tags.map((tag) => (
              <TagElement
                variant='default'
                key={tag.id}
                tag={tag}
                closable={true}
                onRemove={removeTag}
              />
            ))}
          <TextField
            variant='integrated'
            viewOnly={disabledSearch}
            className={classNames(styles.search, {
              [styles.formField]: isFormFieldVariant,
            })}
            value={searchStore.term}
            onChange={searchStore.setTerm}
            InputProps={{
              placeholder: searchPlaceholder,
            }}
            ariaLabel='TagsTextFieldSearchInput'
            mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
          />
        </div>
      </Scrollbar>
      <div className={styles.divider}></div>
      <div
        className={classNames(styles.searchTagsListWrap, {
          [styles.formField]: isFormFieldVariant,
        })}
        aria-label='TagsListItems'
      >
        <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={190}>
          {loading && (
            <SpinnerLoader ariaLabel={'TagsDropdownContent'} className={styles.spinner} />
          )}
          <div className={styles.searchTagsList}>
            {searchStore.hasTags &&
              searchTagsList.map((tag) =>
                isFormFieldVariant ? (
                  <TagDropdownCard
                    key={tag.id}
                    item={tag}
                    showContactsCount={showContactsCount}
                    onChange={() => {
                      addTag(tag)
                    }}
                  />
                ) : (
                  <DropdownCard
                    key={tag.id}
                    item={{
                      id: tag.id,
                      label: tag.label,
                      ariaLabel: 'TagsListItem',
                    }}
                    onChange={() => {
                      addTag(tag)
                    }}
                  />
                )
              )}
            {!cantCreate && <CreateTagButton term={searchStore.term} onClick={createTag} />}
          </div>
          <InView onChange={(inView) => inView && loadMore()}>
            {({ ref }) => (
              <div ref={ref}>
                {searchStore.hasMore && (
                  <SpinnerLoader ariaLabel={'TagsDropdownContent'} className={styles.spinner} />
                )}
                {searchStore.noResults && !searchStore.loading && cantCreate && (
                  <div className={styles.noResults}>No results found</div>
                )}
              </div>
            )}
          </InView>
        </Scrollbar>
      </div>
    </DropdownContent>
  )
})
